import React from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, Sparkles, CheckCircle } from "lucide-react";
import PhoneFrame from "../components/PhoneFrame.jsx";
import { useAuth } from "../lib/auth.jsx";

const STAGES = [
  { key: "awareness", title: "Build Awareness", days: "Days 1-30", desc: "Learn posture fundamentals and establish habits." },
  { key: "consistency", title: "Build Consistency", days: "Days 31-90", desc: "Create sustainable daily movement and posture routines." },
  { key: "strength", title: "Build Strength", days: "Days 91-180", desc: "Develop stronger posture, mobility, and endurance." },
  { key: "lifestyle", title: "Build Lifestyle", days: "Days 181-300", desc: "Integrate posture and wellness into everyday life." },
  { key: "mastery", title: "Master Your Posture", days: "Days 301-365", desc: "Long-term wellness through movement, alignment and habits." },
];

export default function Journey() {
  const nav = useNavigate();
  const { user } = useAuth();
  const day = user?.streak ?? 0;

  return (
    <PhoneFrame>
      <div className="px-5 pt-6 pb-10">
        <button onClick={() => nav(-1)} className="text-slate-400 inline-flex items-center gap-1 text-sm" data-testid="journey-back">
          <ChevronLeft size={16} /> Back
        </button>
        <h1 className="ps-heading text-3xl font-bold mt-4">365-Day Journey</h1>
        <p className="text-slate-400 text-sm">Five stages. One sustained transformation.</p>

        <div className="mt-5 ps-card p-5">
          <div className="text-xs uppercase tracking-widest text-[#2fbf71] font-bold">Your progress</div>
          <div className="ps-heading text-3xl font-bold mt-1" data-testid="journey-day">Day {day} of 365</div>
          <div className="mt-3 h-3 rounded-full bg-[#142534]">
            <div className="h-full rounded-full bg-gradient-to-r from-[#2fbf71] to-[#00B4D8]" style={{ width: `${(day / 365 * 100).toFixed(1)}%` }} />
          </div>
        </div>

        <div className="mt-6 space-y-3">
          {STAGES.map((s, i) => {
            const start = [1, 31, 91, 181, 301][i];
            const end = [30, 90, 180, 300, 365][i];
            const done = day >= end;
            const active = day >= start && day <= end;
            return (
              <div key={s.key} className={`ps-card p-5 ${active ? "border-[#2fbf71]" : ""}`} data-testid={`stage-${s.key}`}>
                <div className="flex items-start gap-4">
                  <div className={`w-12 h-12 rounded-2xl flex items-center justify-center font-bold ps-heading text-xl ${done ? "bg-[#2fbf71] text-[#0a1822]" : active ? "bg-[#142534] text-[#2fbf71]" : "bg-[#142534] text-slate-500"}`}>
                    {done ? <CheckCircle size={22} /> : i + 1}
                  </div>
                  <div className="flex-1">
                    <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">{s.days}</div>
                    <div className="ps-heading font-semibold mt-0.5">{s.title}</div>
                    <div className="text-sm text-slate-400 mt-1">{s.desc}</div>
                    {active && <div className="mt-2 text-xs text-[#2fbf71] inline-flex items-center gap-1"><Sparkles size={12} /> Current stage</div>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </PhoneFrame>
  );
}
